import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';

import {
  giftCouponRedemptionListDataAtom,
  giftCouponRedemptionListStatusAtom,
} from '@shared/api/giftCoupon/giftCoupon.atom';
import {
  DeliverCouponDto,
  deliverCoupon,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import { useGiftCouponRedemptionListHook } from '@shared/api/giftCoupon/giftCoupon.hook';
import {
  GiftCouponForRedemption,
  GiftCouponRedeemStatus,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { Loading } from '@shared/components/Loading';
import { LowerRole } from '@shared/types';
import { fileBucketUrl } from '@shared/utils/fileUtils';

// 상태별 뱃지 스타일
const getStatusBadge = (status: GiftCouponRedeemStatus) => {
  switch (status) {
    case GiftCouponRedeemStatus.Requested:
      return { text: '신청됨', className: 'bg-yellow-100 text-yellow-800' };
    case GiftCouponRedeemStatus.Delivered:
      return { text: '발송완료', className: 'bg-green-100 text-green-800' };
    case GiftCouponRedeemStatus.Expired:
      return { text: '만료', className: 'bg-gray-100 text-gray-800' };
    case GiftCouponRedeemStatus.Refunded:
      return { text: '환불', className: 'bg-red-100 text-red-800' };
    case GiftCouponRedeemStatus.Canceled:
      return { text: '취소', className: 'bg-gray-100 text-gray-500' };
    default:
      return { text: '대기', className: 'bg-blue-100 text-blue-800' };
  }
};

// 2글자 년도 포맷 (25.01.15 14시)
const formatDateTime = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  const y = String(d.getFullYear()).slice(2);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  return `${y}.${m}.${day} ${h}시`;
};

// 2글자 년도 포맷 (25.01.15)
const formatDate = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  const y = String(d.getFullYear()).slice(2);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
};

export const GiftCouponRedemptionListPage: FC = function GiftCouponRedemptionListPage() {
  // 데이터 조회
  useGiftCouponRedemptionListHook(LowerRole.Admin);
  const status = useRecoilValue(giftCouponRedemptionListStatusAtom);
  const [data, setData] = useRecoilState(giftCouponRedemptionListDataAtom);

  // 필터 상태
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [productFilter, setProductFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchText, setSearchText] = useState('');
  const [deliveringId, setDeliveringId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [duplicateOnly, setDuplicateOnly] = useState(false);
  const [duplicateDetailPhone, setDuplicateDetailPhone] = useState<string | null>(null);

  // 토스트 상태
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((msg: string) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToastMessage(msg);
    toastTimer.current = setTimeout(() => setToastMessage(null), 1200);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, []);

  const copyToClipboard = useCallback(
    (text: string) => {
      navigator.clipboard.writeText(text);
      showToast('복사되었습니다.');
    },
    [showToast],
  );

  // 발송 모달 상태
  const [deliverModal, setDeliverModal] = useState<{
    couponIds: string[];
    priceRetail: number;
    priceWhole: string;
    priceRebate: string;
    isBulk: boolean;
  } | null>(null);

  // choiceUnit의 priceRegular 가져오기
  const getChoiceUnitPriceRegular = useCallback(
    (couponId: string) => {
      const coupon = data.find((c) => c._id === couponId);
      if (
        coupon?.selectedChoiceUnit &&
        typeof coupon.selectedChoiceUnit === 'object' &&
        coupon.selectedChoiceUnit.priceRegular
      ) {
        return coupon.selectedChoiceUnit.priceRegular;
      }
      if (typeof coupon?.giftItem === 'object') {
        return coupon.giftItem?.priceRegular || 0;
      }
      return 0;
    },
    [data],
  );

  // 단일 발송 모달 열기
  const openDeliverModal = useCallback(
    (couponId: string) => {
      const priceRetail = getChoiceUnitPriceRegular(couponId);
      setDeliverModal({
        couponIds: [couponId],
        priceRetail,
        priceWhole: '',
        priceRebate: '',
        isBulk: false,
      });
    },
    [getChoiceUnitPriceRegular],
  );

  // 일괄 발송 모달 열기
  const openBulkDeliverModal = useCallback(() => {
    const targetIds = Array.from(selectedIds);
    if (targetIds.length === 0) {
      alert('선택된 항목이 없습니다.');
      return;
    }
    const firstId = targetIds[0];
    if (!firstId) return;
    const priceRetail = getChoiceUnitPriceRegular(firstId);
    setDeliverModal({
      couponIds: targetIds,
      priceRetail,
      priceWhole: '',
      priceRebate: '',
      isBulk: true,
    });
  }, [selectedIds, getChoiceUnitPriceRegular]);

  // 발송 확정 처리
  const handleConfirmDeliver = useCallback(async () => {
    if (!deliverModal) return;

    const priceWhole = Number(deliverModal.priceWhole);
    const priceRebate = Number(deliverModal.priceRebate);

    if (!deliverModal.priceWhole || isNaN(priceWhole)) {
      alert('도매가를 입력해주세요.');
      return;
    }
    if (!deliverModal.priceRebate || isNaN(priceRebate)) {
      alert('리베이트를 입력해주세요.');
      return;
    }

    const dto: DeliverCouponDto = {
      priceWhole,
      priceRetail: deliverModal.priceRetail,
      priceRebate,
    };

    if (deliverModal.isBulk) {
      setIsBulkProcessing(true);
      setDeliverModal(null);
      let successCount = 0;
      let failCount = 0;

      for (const couponId of deliverModal.couponIds) {
        try {
          const updated = await deliverCoupon(couponId, dto);
          setData((prev) =>
            prev.map((item) =>
              item._id === couponId
                ? {
                    ...item,
                    redeemStatus: updated.redeemStatus,
                    gifticon: updated.gifticon,
                  }
                : item,
            ),
          );
          successCount++;
        } catch (error) {
          console.error(`발송 처리 실패 (${couponId}):`, error);
          failCount++;
        }
      }

      setIsBulkProcessing(false);
      setSelectedIds(new Set());

      if (failCount === 0) {
        alert(`${successCount}건 발송 처리 완료`);
      } else {
        alert(`성공: ${successCount}건, 실패: ${failCount}건`);
      }
    } else {
      const couponId = deliverModal.couponIds[0] ?? '';
      setDeliveringId(couponId);
      setDeliverModal(null);

      try {
        const updated = await deliverCoupon(couponId, dto);
        setData((prev) =>
          prev.map((item) =>
            item._id === couponId
              ? {
                  ...item,
                  redeemStatus: updated.redeemStatus,
                  gifticon: updated.gifticon,
                }
              : item,
          ),
        );
        alert('발송 처리가 완료되었습니다.');
      } catch (error) {
        console.error('발송 처리 실패:', error);
        alert('발송 처리에 실패했습니다.');
      } finally {
        setDeliveringId(null);
      }
    }
  }, [deliverModal, setData]);

  // 체크박스 토글
  const handleToggleSelect = useCallback((couponId: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(couponId)) {
        newSet.delete(couponId);
      } else {
        newSet.add(couponId);
      }
      return newSet;
    });
  }, []);

  // 전화번호별 중복 신청 맵 (전체 data 기준, Requested + Delivered 포함)
  const duplicatePhoneMap = useMemo(() => {
    const phoneMap = new Map<string, GiftCouponForRedemption[]>();
    for (const coupon of data) {
      const phone = coupon.recipient?.phoneNumber?.replace(/[^0-9]/g, '');
      if (!phone) continue;
      if (
        coupon.redeemStatus !== GiftCouponRedeemStatus.Requested &&
        coupon.redeemStatus !== GiftCouponRedeemStatus.Delivered
      )
        continue;
      const existing = phoneMap.get(phone) || [];
      existing.push(coupon);
      phoneMap.set(phone, existing);
    }
    // 2건 이상만 중복으로 간주
    const result = new Map<string, GiftCouponForRedemption[]>();
    Array.from(phoneMap.entries()).forEach(([phone, coupons]) => {
      if (coupons.length >= 2) {
        result.set(phone, coupons);
      }
    });
    return result;
  }, [data]);

  // 특정 쿠폰이 중복 신청자인지 확인
  const getDuplicateCount = useCallback(
    (coupon: GiftCouponForRedemption) => {
      const phone = coupon.recipient?.phoneNumber?.replace(/[^0-9]/g, '');
      if (!phone) return 0;
      return duplicatePhoneMap.get(phone)?.length || 0;
    },
    [duplicatePhoneMap],
  );

  // 중복 상세 모달용 데이터
  const duplicateDetailCoupons = useMemo(() => {
    if (!duplicateDetailPhone) return [];
    const phone = duplicateDetailPhone.replace(/[^0-9]/g, '');
    return duplicatePhoneMap.get(phone) || [];
  }, [duplicateDetailPhone, duplicatePhoneMap]);

  // 중복 통계
  const duplicateStats = useMemo(() => {
    let duplicatePhones = 0;
    let duplicateCoupons = 0;
    Array.from(duplicatePhoneMap.values()).forEach((coupons) => {
      duplicatePhones++;
      duplicateCoupons += coupons.length;
    });
    return { duplicatePhones, duplicateCoupons };
  }, [duplicatePhoneMap]);

  // 상태 필터 변경 시 상품 필터 초기화
  const handleStatusFilterChange = useCallback((value: string) => {
    setStatusFilter(value);
    setProductFilter('all');
  }, []);

  // 현재 상태 필터에 해당하는 상품(choiceUnit/giftItem) 옵션 목록
  const productOptions = useMemo(() => {
    const statusFiltered =
      statusFilter === 'all'
        ? data
        : data.filter((item) => item.redeemStatus === statusFilter);

    const optionMap = new Map<string, { key: string; label: string; count: number }>();

    for (const coupon of statusFiltered) {
      let key: string;
      let label: string;

      if (
        coupon.selectedChoiceUnit &&
        typeof coupon.selectedChoiceUnit === 'object' &&
        coupon.selectedChoiceUnit._id
      ) {
        key = `unit:${coupon.selectedChoiceUnit._id}`;
        const itemName =
          typeof coupon.giftItem === 'object'
            ? coupon.giftItem?.name || coupon.giftItem?.giftItemInfo?.name || ''
            : '';
        label = itemName
          ? `${itemName} - ${coupon.selectedChoiceUnit.name}`
          : coupon.selectedChoiceUnit.name;
      } else if (typeof coupon.giftItem === 'object' && coupon.giftItem?._id) {
        key = `item:${coupon.giftItem._id}`;
        label = coupon.giftItem?.name || coupon.giftItem?.giftItemInfo?.name || '-';
      } else {
        continue;
      }

      const existing = optionMap.get(key);
      if (existing) {
        existing.count++;
      } else {
        optionMap.set(key, { key, label, count: 1 });
      }
    }

    return Array.from(optionMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [data, statusFilter]);

  // 필터링된 데이터
  const filteredData = useMemo(() => {
    let result = [...data];

    // 상태 필터
    if (statusFilter !== 'all') {
      result = result.filter((item) => item.redeemStatus === statusFilter);
    }

    // 상품 필터
    if (productFilter !== 'all') {
      result = result.filter((item) => {
        if (productFilter.startsWith('unit:')) {
          const unitId = productFilter.slice(5);
          return (
            item.selectedChoiceUnit &&
            typeof item.selectedChoiceUnit === 'object' &&
            item.selectedChoiceUnit._id === unitId
          );
        } else if (productFilter.startsWith('item:')) {
          const itemId = productFilter.slice(5);
          return typeof item.giftItem === 'object' && item.giftItem?._id === itemId;
        }
        return true;
      });
    }

    // 날짜 필터
    if (dateFilter === 'custom') {
      const from = dateFrom ? new Date(dateFrom) : null;
      const to = dateTo ? new Date(dateTo) : null;
      result = result.filter((item) => {
        if (!item.recipient?.requestedAt) return false;
        const reqDate = new Date(item.recipient.requestedAt);
        if (from && reqDate < from) return false;
        if (to && reqDate > to) return false;
        return true;
      });
    } else if (dateFilter !== 'all') {
      const now = new Date();
      const filterDate = new Date();

      switch (dateFilter) {
        case 'today':
          filterDate.setHours(0, 0, 0, 0);
          result = result.filter(
            (item) =>
              item.recipient?.requestedAt &&
              new Date(item.recipient.requestedAt) >= filterDate,
          );
          break;
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          result = result.filter(
            (item) =>
              item.recipient?.requestedAt &&
              new Date(item.recipient.requestedAt) >= filterDate,
          );
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          result = result.filter(
            (item) =>
              item.recipient?.requestedAt &&
              new Date(item.recipient.requestedAt) >= filterDate,
          );
          break;
      }
    }

    // 검색어 필터 (전화번호, 상품명)
    if (searchText) {
      const search = searchText.toLowerCase().replace(/-/g, '');
      result = result.filter((item) => {
        const phone = item.recipient?.phoneNumber?.replace(/-/g, '') || '';
        const itemName =
          typeof item.giftItem === 'object'
            ? (
                item.giftItem?.name ||
                item.giftItem?.giftItemInfo?.name ||
                ''
              ).toLowerCase()
            : '';
        const unitName =
          item.selectedChoiceUnit && typeof item.selectedChoiceUnit === 'object'
            ? item.selectedChoiceUnit.name?.toLowerCase() || ''
            : '';
        return (
          phone.includes(search) || itemName.includes(search) || unitName.includes(search)
        );
      });
    }

    // 중복 신청만 보기
    if (duplicateOnly) {
      result = result.filter((item) => {
        const phone = item.recipient?.phoneNumber?.replace(/[^0-9]/g, '');
        return phone ? duplicatePhoneMap.has(phone) : false;
      });
    }

    // 상태 우선순위 정렬: 신청됨 > 발송완료 > 나머지, 같은 상태 내 최신순
    const statusPriority: Record<string, number> = {
      [GiftCouponRedeemStatus.Requested]: 0,
      [GiftCouponRedeemStatus.Delivered]: 1,
    };
    result.sort((a, b) => {
      const priorityA = statusPriority[a.redeemStatus] ?? 2;
      const priorityB = statusPriority[b.redeemStatus] ?? 2;
      if (priorityA !== priorityB) return priorityA - priorityB;
      const dateA = a.recipient?.requestedAt
        ? new Date(a.recipient.requestedAt).getTime()
        : 0;
      const dateB = b.recipient?.requestedAt
        ? new Date(b.recipient.requestedAt).getTime()
        : 0;
      return dateB - dateA;
    });

    return result;
  }, [
    data,
    statusFilter,
    productFilter,
    dateFilter,
    dateFrom,
    dateTo,
    searchText,
    duplicateOnly,
    duplicatePhoneMap,
  ]);

  // 통계
  const stats = useMemo(() => {
    const requested = data.filter(
      (d) => d.redeemStatus === GiftCouponRedeemStatus.Requested,
    ).length;
    const delivered = data.filter(
      (d) => d.redeemStatus === GiftCouponRedeemStatus.Delivered,
    ).length;
    const total = data.length;
    return { requested, delivered, total };
  }, [data]);

  // 선택 가능한 항목들 (Requested 상태만)
  const selectableItems = useMemo(
    () =>
      filteredData.filter(
        (item) => item.redeemStatus === GiftCouponRedeemStatus.Requested,
      ),
    [filteredData],
  );

  // 전체 선택 여부
  const isAllSelected = useMemo(
    () =>
      selectableItems.length > 0 &&
      selectableItems.every((item) => selectedIds.has(item._id)),
    [selectableItems, selectedIds],
  );

  // 전체 선택/해제
  const handleToggleAll = useCallback(() => {
    if (isAllSelected) {
      // 전체 해제
      setSelectedIds(new Set());
    } else {
      // 전체 선택
      setSelectedIds(new Set(selectableItems.map((item) => item._id)));
    }
  }, [isAllSelected, selectableItems]);

  // 전화번호 포맷팅
  const formatPhone = useCallback((phone: string | undefined) => {
    if (!phone) return '-';
    const numbers = phone.replace(/[^0-9]/g, '');
    if (numbers.length === 11) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    }
    return phone;
  }, []);

  // 주문자 정보 가져오기
  const getOrdererInfo = useCallback((coupon: GiftCouponForRedemption) => {
    const order = typeof coupon.giftOrder === 'object' ? coupon.giftOrder : null;
    if (!order) return { name: '-', phone: '-', purchasedAt: undefined };
    return {
      name: order.bugo?.member?.fullName || '-',
      phone: order.bugo?.member?.phoneNumber || '-',
      purchasedAt: order.paymentDetail?.purchasedAt,
    };
  }, []);

  // 상품명 가져오기
  const getItemName = useCallback((coupon: GiftCouponForRedemption) => {
    if (typeof coupon.giftItem === 'object') {
      return coupon.giftItem?.name || coupon.giftItem?.giftItemInfo?.name || '-';
    }
    return '-';
  }, []);

  // 선택 유닛명 가져오기
  const getChoiceUnitName = useCallback((coupon: GiftCouponForRedemption) => {
    if (coupon.selectedChoiceUnit && typeof coupon.selectedChoiceUnit === 'object') {
      return coupon.selectedChoiceUnit.name;
    }
    return null;
  }, []);

  // 가격 정보 가져오기
  const getPriceInfo = useCallback((coupon: GiftCouponForRedemption) => {
    if (typeof coupon.giftItem === 'object') {
      return {
        priceRegular: coupon.giftItem?.priceRegular || 0,
        priceRetail: coupon.giftItem?.priceRetail || 0,
        priceWhole: coupon.giftItem?.priceWhole || 0,
        priceRebate: coupon.giftItem?.priceRebate || 0,
      };
    }
    return { priceRegular: 0, priceRetail: 0, priceWhole: 0, priceRebate: 0 };
  }, []);

  // 이미지 URL 가져오기
  const getImageUrl = useCallback((coupon: GiftCouponForRedemption) => {
    // 선택 유닛 이미지 우선
    if (
      coupon.selectedChoiceUnit &&
      typeof coupon.selectedChoiceUnit === 'object' &&
      coupon.selectedChoiceUnit.image
    ) {
      const img = coupon.selectedChoiceUnit.image;
      const imgUrl = typeof img === 'object' && 'url' in img ? img.url : img;
      if (imgUrl) return fileBucketUrl(imgUrl, 'w200');
    }
    // 상품 이미지
    if (typeof coupon.giftItem === 'object') {
      // giftItem.image 먼저
      const giftItemImg = coupon.giftItem?.image;
      if (giftItemImg) {
        const imgUrl =
          typeof giftItemImg === 'object' && 'url' in giftItemImg
            ? giftItemImg.url
            : giftItemImg;
        if (imgUrl) return fileBucketUrl(imgUrl, 'w200');
      }
      // giftItemInfo.image
      const giftItemInfoImg = coupon.giftItem?.giftItemInfo?.image;
      if (giftItemInfoImg) {
        const imgUrl =
          typeof giftItemInfoImg === 'object' && 'url' in giftItemInfoImg
            ? giftItemInfoImg.url
            : giftItemInfoImg;
        if (imgUrl) return fileBucketUrl(imgUrl, 'w200');
      }
    }
    return '';
  }, []);

  if (status === 'loading' || status === 'idle') {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <h1 className="text-xl font-bold">쿠폰 교환 신청 확인</h1>
        <p className="mt-1 text-sm text-gray-500">
          교환 신청된 쿠폰을 확인하고 기프티콘을 발송하세요.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 p-4">
        <button
          onClick={() => {
            const target = GiftCouponRedeemStatus.Requested;
            if (statusFilter === target) {
              setStatusFilter('all');
            } else {
              setStatusFilter(target);
              setProductFilter('all');
              setDuplicateOnly(false);
            }
          }}
          className={`rounded-lg bg-yellow-50 p-4 text-center transition-colors hover:bg-yellow-100 ${
            statusFilter === GiftCouponRedeemStatus.Requested ? 'ring-2 ring-yellow-400' : ''
          }`}
        >
          <p className="text-2xl font-bold text-yellow-700">{stats.requested}</p>
          <p className="text-sm text-yellow-600">발송 대기</p>
        </button>
        <button
          onClick={() => {
            const target = GiftCouponRedeemStatus.Delivered;
            if (statusFilter === target) {
              setStatusFilter('all');
            } else {
              setStatusFilter(target);
              setProductFilter('all');
              setDuplicateOnly(false);
            }
          }}
          className={`rounded-lg bg-green-50 p-4 text-center transition-colors hover:bg-green-100 ${
            statusFilter === GiftCouponRedeemStatus.Delivered ? 'ring-2 ring-green-400' : ''
          }`}
        >
          <p className="text-2xl font-bold text-green-700">{stats.delivered}</p>
          <p className="text-sm text-green-600">발송 완료</p>
        </button>
        <button
          onClick={() => {
            if (statusFilter === 'all' && !duplicateOnly) {
              return;
            }
            setStatusFilter('all');
            setProductFilter('all');
            setDuplicateOnly(false);
          }}
          className={`rounded-lg bg-gray-100 p-4 text-center transition-colors hover:bg-gray-200 ${
            statusFilter === 'all' && !duplicateOnly ? 'ring-2 ring-gray-400' : ''
          }`}
        >
          <p className="text-2xl font-bold text-gray-700">{stats.total}</p>
          <p className="text-sm text-gray-600">전체</p>
        </button>
        <button
          onClick={() => {
            if (duplicateOnly) {
              setDuplicateOnly(false);
            } else {
              setDuplicateOnly(true);
              setStatusFilter('all');
              setProductFilter('all');
            }
          }}
          className={`rounded-lg p-4 text-center transition-colors ${
            duplicateStats.duplicatePhones > 0
              ? 'bg-red-50 hover:bg-red-100'
              : 'bg-gray-50'
          } ${duplicateOnly ? 'ring-2 ring-red-400' : ''}`}
        >
          <p
            className={`text-2xl font-bold ${
              duplicateStats.duplicatePhones > 0 ? 'text-red-700' : 'text-gray-400'
            }`}
          >
            {duplicateStats.duplicatePhones}
          </p>
          <p
            className={`text-sm ${
              duplicateStats.duplicatePhones > 0 ? 'text-red-600' : 'text-gray-400'
            }`}
          >
            중복 의심 ({duplicateStats.duplicateCoupons}건)
          </p>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white px-4 py-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          {/* 상태 필터 */}
          <select
            value={statusFilter}
            onChange={(e) => handleStatusFilterChange(e.target.value)}
            className="rounded-lg border px-3 py-2 text-sm"
          >
            <option value="all">전체 상태</option>
            <option value={GiftCouponRedeemStatus.Requested}>신청됨 (발송대기)</option>
            <option value={GiftCouponRedeemStatus.Delivered}>발송완료</option>
            <option value={GiftCouponRedeemStatus.Expired}>만료</option>
            <option value={GiftCouponRedeemStatus.Refunded}>환불</option>
          </select>
          {/* 상품 필터 */}
          <select
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value)}
            className="max-w-[260px] rounded-lg border px-3 py-2 text-sm"
          >
            <option value="all">전체 상품</option>
            {productOptions.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.label} ({opt.count})
              </option>
            ))}
          </select>
          {/* 날짜 필터 */}
          <select
            value={dateFilter}
            onChange={(e) => {
              setDateFilter(e.target.value);
              if (e.target.value !== 'custom') {
                setDateFrom('');
                setDateTo('');
              }
            }}
            className="rounded-lg border px-3 py-2 text-sm"
          >
            <option value="all">전체 기간</option>
            <option value="today">오늘</option>
            <option value="week">최근 1주</option>
            <option value="month">최근 1개월</option>
            <option value="custom">직접 지정</option>
          </select>

          {dateFilter === 'custom' && (
            <>
              <input
                type="datetime-local"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="rounded-lg border px-3 py-2 text-sm"
              />
              <span className="text-sm text-gray-400">~</span>
              <input
                type="datetime-local"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="rounded-lg border px-3 py-2 text-sm"
              />
            </>
          )}

          {/* 검색 */}
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="전화번호, 상품명 검색"
            className="flex-1 rounded-lg border px-3 py-2 text-sm"
          />
        </div>

        {/* 일괄 처리 영역 */}
        {selectedIds.size > 0 && (
          <div className="mt-3 flex items-center gap-3 rounded-lg bg-indigo-50 px-4 py-3">
            <span className="text-sm font-medium text-indigo-700">
              {selectedIds.size}건 선택됨
            </span>
            <button
              onClick={openBulkDeliverModal}
              disabled={isBulkProcessing}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isBulkProcessing ? '처리 중...' : '일괄 발송처리'}
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              선택 해제
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="p-4">
        {filteredData.length > 0 ? (
          <div className="overflow-x-auto rounded-lg bg-white shadow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-12 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={handleToggleAll}
                      disabled={selectableItems.length === 0}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    상태
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    쿠폰번호
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    상품
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    정가
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    판매가
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    도매가
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    주문자
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    구매일
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <div className="flex items-center gap-1.5">
                      수신자 정보
                      {productFilter !== 'all' && (
                        <button
                          onClick={() => {
                            const phones = filteredData
                              .map((c) => c.recipient?.phoneNumber)
                              .filter((p): p is string => !!p)
                              .map((p) => p.replace(/[^0-9]/g, ''))
                              .filter((p) => p.length > 0);
                            if (phones.length === 0) {
                              showToast('복사할 번호가 없습니다.');
                              return;
                            }
                            copyToClipboard(phones.join('\n'));
                          }}
                          title={`필터된 수신자 번호 전체 복사 (${filteredData.length}건)`}
                          className="inline-flex items-center gap-1 rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-bold normal-case tracking-normal text-indigo-700 hover:bg-indigo-200"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3 w-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                          </svg>
                          번호 복사 ({filteredData.length})
                        </button>
                      )}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    신청일시
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    만료일
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    액션
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredData.map((coupon) => {
                  const badge = getStatusBadge(coupon.redeemStatus);
                  const choiceUnitName = getChoiceUnitName(coupon);
                  const priceInfo = getPriceInfo(coupon);
                  const ordererInfo = getOrdererInfo(coupon);

                  const isSelectable =
                    coupon.redeemStatus === GiftCouponRedeemStatus.Requested;
                  const isSelected = selectedIds.has(coupon._id);

                  return (
                    <tr
                      key={coupon._id}
                      className={`hover:bg-gray-50 ${isSelected ? 'bg-indigo-50' : ''}`}
                    >
                      {/* 체크박스 */}
                      <td className="px-4 py-3">
                        {isSelectable ? (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleSelect(coupon._id)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        ) : (
                          <span className="block h-4 w-4" />
                        )}
                      </td>

                      {/* 상태 */}
                      <td className="whitespace-nowrap px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${badge.className}`}
                        >
                          {badge.text}
                        </span>
                      </td>

                      {/* 쿠폰번호 */}
                      <td className="whitespace-nowrap px-4 py-3">
                        <span className="font-mono text-sm text-gray-700">
                          {coupon.fullCode || '-'}
                        </span>
                      </td>

                      {/* 상품 */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {getImageUrl(coupon) && (
                            <img
                              src={getImageUrl(coupon)}
                              alt=""
                              className="h-10 w-10 rounded object-cover"
                            />
                          )}
                          <div>
                            <div className="flex items-center gap-1">
                              <p className="text-sm font-medium text-gray-900">
                                {getItemName(coupon)}
                              </p>
                              {choiceUnitName &&
                                (coupon.selectedChoiceUnit &&
                                typeof coupon.selectedChoiceUnit === 'object' &&
                                coupon.selectedChoiceUnit.wholeUrl ? (
                                  <a
                                    href={coupon.selectedChoiceUnit.wholeUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={`도매사이트 이동: ${choiceUnitName}`}
                                    className="shrink-0 rounded p-0.5 text-indigo-500 hover:bg-indigo-50 hover:text-indigo-700"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-3.5 w-3.5"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                      strokeWidth={2}
                                    >
                                      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                                      <polyline points="15 3 21 3 21 9" />
                                      <line x1="10" y1="14" x2="21" y2="3" />
                                    </svg>
                                  </a>
                                ) : (
                                  <button
                                    onClick={() => copyToClipboard(choiceUnitName)}
                                    title={choiceUnitName}
                                    className="shrink-0 rounded p-0.5 text-indigo-500 hover:bg-indigo-50 hover:text-indigo-700"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-3.5 w-3.5"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                      strokeWidth={2}
                                    >
                                      <rect
                                        x="9"
                                        y="9"
                                        width="13"
                                        height="13"
                                        rx="2"
                                        ry="2"
                                      />
                                      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                                    </svg>
                                  </button>
                                ))}
                            </div>
                            {choiceUnitName && (
                              <p className="max-w-[200px] text-xs text-indigo-600">
                                {choiceUnitName}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* 정가 */}
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-500">
                        {priceInfo.priceRegular.toLocaleString()}원
                      </td>

                      {/* 판매가 */}
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900">
                        {priceInfo.priceRetail.toLocaleString()}원
                      </td>

                      {/* 도매가 */}
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-indigo-600">
                        {coupon.redeemStatus === GiftCouponRedeemStatus.Delivered &&
                        coupon.gifticon?.priceWhole != null
                          ? coupon.gifticon.priceWhole.toLocaleString()
                          : priceInfo.priceWhole.toLocaleString()}
                        원
                      </td>

                      {/* 주문자 */}
                      <td className="whitespace-nowrap px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">
                          {ordererInfo.name}
                        </p>
                      </td>

                      {/* 구매일 */}
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                        {formatDate(ordererInfo.purchasedAt)}
                      </td>

                      {/* 수신자 정보 */}
                      <td className="whitespace-nowrap px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => {
                              const phone = coupon.recipient?.phoneNumber;
                              if (phone) copyToClipboard(phone);
                            }}
                            className="text-sm font-medium text-gray-900 hover:text-indigo-600"
                            title="클릭하여 복사"
                          >
                            {formatPhone(coupon.recipient?.phoneNumber)}
                          </button>
                          {getDuplicateCount(coupon) >= 2 && (
                            <button
                              onClick={() =>
                                setDuplicateDetailPhone(
                                  coupon.recipient?.phoneNumber || null,
                                )
                              }
                              className="inline-flex items-center gap-0.5 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700 hover:bg-red-200"
                              title="중복 신청 의심 - 클릭하여 상세 확인"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3 w-3"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              {getDuplicateCount(coupon)}건
                            </button>
                          )}
                        </div>
                        {coupon.recipient?.name && (
                          <p className="text-xs text-gray-500">{coupon.recipient.name}</p>
                        )}
                      </td>

                      {/* 신청일시 */}
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                        {formatDateTime(coupon.recipient?.requestedAt)}
                      </td>

                      {/* 만료일 */}
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                        {formatDate(coupon.expiresAt)}
                      </td>

                      {/* 액션 */}
                      <td className="whitespace-nowrap px-4 py-3">
                        {coupon.redeemStatus === GiftCouponRedeemStatus.Requested && (
                          <button
                            onClick={() => openDeliverModal(coupon._id)}
                            disabled={deliveringId === coupon._id}
                            className="rounded bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {deliveringId === coupon._id ? '처리중...' : '발송처리'}
                          </button>
                        )}
                        {coupon.redeemStatus === GiftCouponRedeemStatus.Delivered && (
                          <span className="text-xs text-gray-400">
                            {formatDateTime(coupon.gifticon?.deliveredAt)}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="rounded-lg bg-white p-8 text-center shadow">
            <p className="text-gray-500">조건에 맞는 교환 신청이 없습니다.</p>
          </div>
        )}
      </div>

      {/* 발송처리 모달 */}
      {deliverModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setDeliverModal(null)}
          />
          <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-bold text-gray-900">
              발송 처리
              {deliverModal.isBulk ? ` (${deliverModal.couponIds.length}건)` : ''}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              가격 정보를 입력한 후 발송 처리하세요.
            </p>

            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  판매가 (priceRetail)
                </label>
                <div className="mt-1 flex items-center rounded-md border bg-gray-50 px-3 py-2 text-sm text-gray-500">
                  {deliverModal.priceRetail.toLocaleString()}원
                  <span className="ml-2 text-xs text-gray-400">(자동)</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  도매가 (priceWhole)
                </label>
                <input
                  type="number"
                  value={deliverModal.priceWhole}
                  onChange={(e) =>
                    setDeliverModal((prev) =>
                      prev ? { ...prev, priceWhole: e.target.value } : null,
                    )
                  }
                  placeholder="도매가 입력"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  리베이트 (priceRebate)
                </label>
                <input
                  type="number"
                  value={deliverModal.priceRebate}
                  onChange={(e) =>
                    setDeliverModal((prev) =>
                      prev ? { ...prev, priceRebate: e.target.value } : null,
                    )
                  }
                  placeholder="리베이트 입력"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setDeliverModal(null)}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={handleConfirmDeliver}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              >
                발송 처리
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 중복 신청 상세 모달 */}
      {duplicateDetailPhone && duplicateDetailCoupons.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setDuplicateDetailPhone(null)}
          />
          <div className="relative max-h-[80vh] w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-2xl">
            <div className="border-b bg-red-50 px-6 py-4">
              <div className="flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-red-600"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <h3 className="text-lg font-bold text-red-800">
                  중복 신청 의심 - {formatPhone(duplicateDetailPhone)}
                </h3>
              </div>
              <p className="mt-1 text-sm text-red-600">
                동일 전화번호로{' '}
                <span className="font-bold">{duplicateDetailCoupons.length}건</span>의
                교환 신청이 감지되었습니다.
              </p>
            </div>
            <div className="max-h-[60vh] overflow-y-auto px-6 py-4">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                      상태
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                      쿠폰번호
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                      상품
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                      수신자명
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                      신청일시
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                      주문자
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {duplicateDetailCoupons
                    .sort((a, b) => {
                      const dateA = a.recipient?.requestedAt
                        ? new Date(a.recipient.requestedAt).getTime()
                        : 0;
                      const dateB = b.recipient?.requestedAt
                        ? new Date(b.recipient.requestedAt).getTime()
                        : 0;
                      return dateB - dateA;
                    })
                    .map((c) => {
                      const badge = getStatusBadge(c.redeemStatus);
                      const orderer = getOrdererInfo(c);
                      return (
                        <tr key={c._id} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap px-3 py-2">
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${badge.className}`}
                            >
                              {badge.text}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-gray-700">
                            {c.fullCode || '-'}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-900">
                            {getItemName(c)}
                            {getChoiceUnitName(c) && (
                              <span className="ml-1 text-indigo-500">
                                ({getChoiceUnitName(c)})
                              </span>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-700">
                            {c.recipient?.name || '-'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500">
                            {formatDateTime(c.recipient?.requestedAt)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-700">
                            {orderer.name}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
            <div className="border-t px-6 py-3">
              <button
                onClick={() => setDuplicateDetailPhone(null)}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 토스트 */}
      {toastMessage && (
        <div className="pointer-events-none fixed inset-x-0 bottom-8 z-[100] flex justify-center">
          <div className="animate-fade-in-out rounded-lg bg-gray-800 px-4 py-2 text-sm font-medium text-white shadow-lg">
            {toastMessage}
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translateY(8px); }
          15% { opacity: 1; transform: translateY(0); }
          75% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-4px); }
        }
        .animate-fade-in-out {
          animation: fadeInOut 1.2s ease-in-out forwards;
        }
      `}</style>
    </div>
  );
};
